import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const WebLayout = ({ children, title, description, keywords }) => {
  useEffect(() => {
    // Web-theme CSS'lerini yükle
    const loadWebThemeCSS = () => {
      const cssFiles = [
        '/web-theme/css/style.min.css',
        '/web-theme/css/ispradius.css',
        '/web-theme/css/icons.min.css',
        '/web-theme/libs/aos/aos.css',
        '/web-theme/libs/swiper/swiper-bundle.min.css'
      ];

      cssFiles.forEach(href => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        document.head.appendChild(link);
      });
    };

    // Web-theme JS'lerini yükle - Sıralı yükleme
    const loadWebThemeJS = () => {
      const jsFiles = [
        '/web-theme/libs/lucide/umd/lucide.min.js',
        '/web-theme/libs/aos/aos.js',
        '/web-theme/libs/gumshoejs/gumshoe.polyfills.min.js',
        '/web-theme/libs/swiper/swiper-bundle.min.js',
        '/web-theme/libs/glightbox/js/glightbox.min.js',
        '/web-theme/libs/preline/preline.js'
      ];

      // Önce temel kütüphaneleri yükle
      let loadedScripts = 0;
      const totalScripts = jsFiles.length;

      jsFiles.forEach((src, index) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => {
          loadedScripts++;
          
          // Tüm temel script'ler yüklendikten sonra theme script'lerini yükle
          if (loadedScripts === totalScripts) {
            setTimeout(() => {
              // Lucide'ı initialize et
              if (window.lucide && window.lucide.createIcons) {
                const initLucide = () => {
                  try {
                    window.lucide.createIcons();
                    window.lucideInitialized = true;
                    window.dispatchEvent(new CustomEvent('lucideReady'));
                  } catch (error) {
                    setTimeout(initLucide, 100);
                  }
                };
                
                initLucide();
              }
              
              // Şimdi theme script'lerini sırayla yükle
              const themeScripts = [
                '/web-theme/js/swiper.js',
                '/web-theme/js/theme.js'
              ];
              
              const loadScriptSequentially = (scripts, index = 0) => {
                if (index >= scripts.length) return;
                
                const script = document.createElement('script');
                script.src = scripts[index];
                script.onload = () => {
                  loadScriptSequentially(scripts, index + 1);
                };
                script.onerror = () => {
                  loadScriptSequentially(scripts, index + 1);
                };
                document.body.appendChild(script);
              };
              
              loadScriptSequentially(themeScripts);
              
            }, 500);
          }
        };
        script.onerror = () => {
          loadedScripts++;
        };
        document.body.appendChild(script);
      });
    };

    // Body class'ını ayarla
    document.body.className = 'bg-slate-900';

    // CSS ve JS'leri yükle
    loadWebThemeCSS();
    loadWebThemeJS();

    // Initialize AOS
    const initAOS = () => {
      if (window.AOS) {
        window.AOS.init();
      }
    };

    // Initialize Lucide Icons
    const initLucide = () => {
      if (window.lucide) {
        window.lucide.createIcons();
      }
    };

    // Sticky Navbar
    const windowScroll = () => {
      const navbar = document.getElementById("navbar-sticky");
      if (navbar) {
        if (document.body.scrollTop >= 50 || document.documentElement.scrollTop >= 50) {
          navbar.classList.add("nav-sticky");
        } else {
          navbar.classList.remove("nav-sticky");
        }
      }
    };

    // Navbar Active Class (Gumshoejs)
    const initGumshoe = () => {
      try {
        if (window.Gumshoe && typeof window.Gumshoe === 'function') {
          const navLinks = document.querySelectorAll('#navbar-navlist a');
          if (navLinks.length > 0) {
            new window.Gumshoe('#navbar-navlist a', {
              offset: 80
            });
          }
        }
      } catch (error) {
        // Gumshoe may not be available on all pages
      }
    };

    // Back To Top
    const scrollFunction = () => {
      const mybutton = document.getElementById("back-to-top");
      if (mybutton) {
        if (document.body.scrollTop > 500 || document.documentElement.scrollTop > 500) {
          mybutton.classList.add("flex");
          mybutton.classList.remove("hidden");
        } else {
          mybutton.classList.add("hidden");
          mybutton.classList.remove("flex");
        }
      }
    };

    // Swiper
    const initSwiper = () => {
      if (window.Swiper) {
        new window.Swiper(".logo-swiper", {
          loop: true,
          spaceBetween: 20,
          slidesPerView: 1,
          autoplay: {
            delay: 2500,
            disableOnInteraction: false,
          },
          breakpoints: {
            640: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
            768: {
              slidesPerView: 3,
              spaceBetween: 40,
            },
            1024: {
              slidesPerView: 4,
              spaceBetween: 50,
            },
            1280: {
              slidesPerView: 5,
              spaceBetween: 60,
            },
          },
        });
      }
    };

    // GLightbox
    const initGLightbox = () => {
      if (window.GLightbox) {
        window.GLightbox({
          selector: '.glightbox'
        });
      }
    };

    // Preline
    const initPreline = () => {
      if (window.HSStaticMethods) {
        window.HSStaticMethods.autoInit();
      }
    };

    // Initialize everything after a short delay to ensure scripts are loaded
    const initAll = () => {
      setTimeout(() => {
        try {
          initAOS();
          initLucide();
          initGumshoe();
          initSwiper();
          initGLightbox();
          initPreline();
        } catch (error) {
          // Some initialization functions may not be needed on all pages
        }
        
        window.addEventListener("scroll", windowScroll);
        window.onscroll = scrollFunction;
      }, 100);
    };

    initAll();

    return () => {
      // Cleanup
      window.removeEventListener("scroll", windowScroll);
      document.body.className = '';
      
      // Remove web-theme CSS
      const webThemeCSS = document.querySelectorAll('link[href*="/web-theme/"]');
      webThemeCSS.forEach(link => link.remove());
      
      // Remove web-theme JS
      const webThemeJS = document.querySelectorAll('script[src*="/web-theme/"]');
      webThemeJS.forEach(script => script.remove());
    };
  }, []);

  const topFunction = () => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  };

  return (
    <>
      <Helmet>
        <title>{title ? `${title} - ISP Radius | ISS Abone Yönetim Sistemi` : 'ISP Radius | ISS Abone Yönetim Sistemi'}</title>
        <meta name="description" content={description || 'ISP Radius, internet servis sağlayıcıları için geliştirilmiş kapsamlı abone yönetim sistemi. BTK loglama, Mikrotik entegrasyonu, otomatik faturalama ve daha fazlası.'} />
        <meta name="keywords" content={keywords || 'ISP Radius, ISS, abone yönetim sistemi, BTK loglama, Mikrotik entegrasyonu, otomatik faturalama, internet servis sağlayıcısı, telekomünikasyon, abone takibi, fatura yönetimi'} />
        <meta name="author" content="ISP Radius" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <meta name="language" content="Turkish" />
        <meta name="revisit-after" content="7 days" />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content={title ? `${title} - ISP Radius | ISS Abone Yönetim Sistemi` : 'ISP Radius | ISS Abone Yönetim Sistemi'} />
        <meta property="og:description" content={description || 'ISP Radius, internet servis sağlayıcıları için geliştirilmiş kapsamlı abone yönetim sistemi.'} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content="ISP Radius" />
        <meta property="og:locale" content="tr_TR" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title ? `${title} - ISP Radius | ISS Abone Yönetim Sistemi` : 'ISP Radius | ISS Abone Yönetim Sistemi'} />
        <meta name="twitter:description" content={description || 'ISP Radius, internet servis sağlayıcıları için geliştirilmiş kapsamlı abone yönetim sistemi.'} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={window.location.href} />
        
        {/* Favicon */}
        <link rel="icon" type="image/x-icon" href="/web-theme/images/favicon.ico" />
      </Helmet>
      
      <div className="App">
      {/* Background Gradients */}
      <div>
        <div className="fixed top-0 -z-10">
          <div className="blur-[200px] w-[600px] h-[600px] rounded-full bg-gradient-to-tl from-violet-600/20 to-indigo-600/20"></div>
        </div>
        <div className="fixed top-0 end-0 -z-10">
          <div className="blur-[200px] w-[500px] h-[500px] rounded-full bg-gradient-to-tl from-blue-600/15 to-cyan-600/15"></div>
        </div>
        <div className="fixed inset-0 flex items-center justify-center -z-10">
          <div className="blur-[250px] w-[600px] h-[600px] rounded-full bg-gradient-to-tl from-purple-600/20 to-violet-600/15"></div>
        </div>
        <div className="fixed bottom-0 start-0 -z-10">
          <div className="blur-[200px] w-[500px] h-[500px] rounded-full bg-gradient-to-tl from-indigo-600/20 to-blue-600/15"></div>
        </div>
        <div className="fixed bottom-0 end-0 -z-10">
          <div className="blur-[200px] w-[500px] h-[500px] rounded-full bg-gradient-to-tl from-violet-600/15 to-purple-600/20"></div>
        </div>
      </div>

      {/* Navbar Start */}
      <header id="navbar-sticky" className="navbar">
        <div className="container">
          <nav>
            <Link to="/" className="logo">
              <img src="/web-theme/images/logo.png" className="h-20" alt="ISP Radius" />
            </Link>

            <div className="lg:hidden flex items-center ms-auto px-2.5">
              <button className="hs-collapse-toggle inline-flex items-center justify-center h-9 w-12 rounded-md border border-white/20 bg-default-100/5" type="button" id="hs-unstyled-collapse" data-hs-collapse="#mobileMenu" data-hs-type="collapse">
                <i data-lucide="menu" className="h-5 w-5 stroke-white"></i>
              </button>
            </div>

            <div id="mobileMenu" className="hs-collapse transition-all duration-300 lg:basis-auto basis-full grow hidden lg:flex items-center justify-center mx-auto mt-2 lg:mt-0">
              <ul id="navbar-navlist" className="navbar-nav">
                <li className="nav-item">
                  <Link to="/" className="nav-link">Anasayfa</Link>
                </li>
                <li className="nav-item">
                  <Link to="/kurumsal" className="nav-link">Kurumsal</Link>
                </li>
                <li className="nav-item">
                  <Link to="/ozellikler" className="nav-link">Özellikler</Link>
                </li>
                <li className="nav-item">
                  <Link to="/entegrasyonlar" className="nav-link">Entegrasyonlar</Link>
                </li>
                <li className="nav-item">
                  <Link to="/iletisim" className="nav-link">İletişim</Link>
                </li>
              </ul>
              <div className="lg:hidden flex items-center pt-4 mt-4 lg:pt-0 lg:mt-0 border-t border-white/10 lg:border-none">
                <Link to="/iletisim" className="inline-flex items-center justify-center gap-2 bg-primary text-white py-2 px-6 rounded-full hover:bg-primary-hover transition-all duration-300">Ücretsiz Demo Talebi</Link>
              </div>
            </div>
            <div className="hidden lg:flex items-center">
              <Link to="/iletisim" className="inline-flex items-center justify-center gap-2 bg-primary text-white py-2 px-6 rounded-full hover:bg-primary-hover transition-all duration-300">Ücretsiz Demo Talebi</Link>
            </div>
          </nav>
        </div>
      </header>
      {/* Navbar End */}

      <main className="flex-grow">
        {children}
      </main>

      {/* Footer section start */}
      <footer className="bg-default-950/60 backdrop-blur-xl border-t border-white/5">
        <div className="container">
          <div className="grid lg:grid-cols-9 grid-cols-2 gap-10 lg:gap-16 py-20">
            <div className="lg:col-span-3 sm:col-span-1 col-span-2">
              <img src="/web-theme/images/logo.png" className="h-20" alt="ISP Radius" />
              <p className="text-sm text-default-300 mt-4 max-w-xs">
                İnternet servis sağlayıcıları için geliştirilmiş kapsamlı abone yönetim sistemi.
              </p>
              <div className="mt-6">
                <ul className="flex flex-wrap items-center gap-2">
                  <li>
                    <a href="https://www.instagram.com/ispradius/" target="_blank" rel="noopener noreferrer" className="h-10 w-10 inline-flex items-center justify-center border border-white/10 rounded-lg transition-all duration-300 group hover:bg-primary hover:border-primary hover:scale-110"><i data-lucide="instagram" className="h-5 w-5 text-default-300 group-hover:text-white"></i></a>
                  </li>
                </ul>
              </div>
            </div>

            <div className="lg:col-span-2 sm:col-span-1 col-span-2">
              <ul className="flex flex-col gap-3">
                <h5 className="xl:text-xl lg:text-lg font-medium text-white mb-2">ISP Radius</h5>
                <li><Link to="/kurumsal" className="text-sm font-normal text-default-300 hover:text-primary transition-all duration-300 hover:translate-x-1 inline-block">Kurumsal</Link></li>
                <li><Link to="/referanslar" className="text-sm font-normal text-default-300 hover:text-primary transition-all duration-300 hover:translate-x-1 inline-block">Referanslar</Link></li>
                <li><Link to="/banka-hesap-bilgileri" className="text-sm font-normal text-default-300 hover:text-primary transition-all duration-300 hover:translate-x-1 inline-block">Banka Hesap Bilgileri</Link></li>
                <li><Link to="/iletisim" className="text-sm font-normal text-default-300 hover:text-primary transition-all duration-300 hover:translate-x-1 inline-block">İletişim</Link></li>
              </ul>
            </div>

            <div className="lg:col-span-2 sm:col-span-1 col-span-2">
              <ul className="flex flex-col gap-3">
                <h5 className="xl:text-xl lg:text-lg font-medium text-white mb-2">Menü</h5>
                <li><Link to="/ozellikler" className="text-sm font-normal text-default-300 hover:text-primary transition-all duration-300 hover:translate-x-1 inline-block">Özellikler</Link></li>
                <li><Link to="/entegrasyonlar" className="text-sm font-normal text-default-300 hover:text-primary transition-all duration-300 hover:translate-x-1 inline-block">Entegrasyonlar</Link></li>
                <li><Link to="/sikca-sorulan-sorular" className="text-sm font-normal text-default-300 hover:text-primary transition-all duration-300 hover:translate-x-1 inline-block">Sıkça Sorulan Sorular</Link></li>
              </ul>
            </div>

            <div className="lg:col-span-2 sm:col-span-1 col-span-2">
              <ul className="flex flex-col gap-3">
                <h5 className="xl:text-xl lg:text-lg font-medium text-white mb-2">Sözleşmeler</h5>
                <li><Link to="/gizlilik-sozlesmesi" className="text-sm font-normal text-default-300 hover:text-primary transition-all duration-300 hover:translate-x-1 inline-block">Gizlilik Sözleşmesi</Link></li>
                <li><Link to="/cerez-sozlesmesi" className="text-sm font-normal text-default-300 hover:text-primary transition-all duration-300 hover:translate-x-1 inline-block">Çerez Sözleşmesi</Link></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-white/5 py-6">
          <div className="container flex flex-wrap justify-center items-center gap-4 text-center">
            <p className="text-sm font-medium text-default-400">
              {new Date().getFullYear()} © ISP Radius — <a href="https://kalemweb.com.tr" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors duration-300"><i data-lucide="heart" className="inline h-3.5 w-3.5 text-red-500 fill-red-500"></i> Kalem Web Yazılım Ltd. Şti.</a> projesidir.
            </p>
          </div>
        </div>
      </footer>
      {/* Footer section End */}

      {/* Back To Top Start */}
      <button id="back-to-top" onClick={topFunction} className="fixed text-xl rounded-full z-10 bottom-5 end-5 h-8 w-8 text-center bg-white backdrop-blur-3xl text-slate-950 flex justify-center items-center transition-all duration-500 hover:bg-primary hover:text-white hover:rounded-lg">
        <i data-lucide="chevron-up" className="h-4 w-4"></i>
      </button>
      {/* Back To Top End */}
      </div>
    </>
  );
};

export default WebLayout;